import { useEffect, useMemo, useRef, useState } from "react";
import { useGameStore } from "../store/useGameStore";
import { useBattleHaptics } from "../hooks/useBattleHaptics";
import { BuffFlyOverlay, useBuffFlyAnimations } from "./BuffFlyOverlay";
import { BuffBar } from "./BuffBar";
import { getHeroImagePath, CLASS_INFO, CLASS_CREST, resolveSkillDisplay, staticAssetUrl } from "../utils/assets";
import {
  battleBarSkillSeed,
  growFrozenBattleSkills,
  mergeFrozenBattleSkills,
  mergeRotationSources,
  tickSkillCooldowns,
} from "../utils/battleRotation";
import { SkillIcon } from "./SkillIcon";
import { FormattedNumber } from "./FormattedNumber";
import type { BattleLogEvent, BuffDefinition, DamageType, Formation, HeroClass, HeroGender, SkillName, StatusEffect } from "../types";
import clsx from "clsx";

const DMG_LIFETIME_MS = 1500;

/* ─── floating combat number (damage / heal) ─────────────────────────────── */
export interface FloatingNumber {
  id: string;
  uid: string;
  value: number;
  kind: "damage" | "heal";
  isCrit: boolean;
  damageType?: DamageType;
}

export interface UnitMeta {
  displayName: string;
  heroClass: HeroClass;
  gender: HeroGender;
  isPlayer: boolean;
  formation?: Formation;
  portraitPath?: string | null;
}

/* ─── per-unit HP/mana state derived from a (partial) battle log ───────── */
export interface UnitState {
  hps: Record<string, number>;
  maxHps: Record<string, number>;
  playerUid: string;
  playerUids: string[];
  /** Ally uids seen in INIT/JOIN (including dead) — drives partyMode. */
  allyRosterUids: string[];
  hasJoin: boolean;
  playerMana: number;
  enemyUids: string[];
  unitMeta: Record<string, UnitMeta>;
  currentSkill: SkillName;
  currentSkillDisplay: string;
  playerCooldowns: Record<string, number>;
  effects: Record<string, StatusEffect[]>;
}

function applySnapshotEffects(
  effects: Record<string, StatusEffect[]>,
  snapshots: BattleLogEvent["snapshots"],
) {
  if (!snapshots) return;
  for (const [uid, snap] of Object.entries(snapshots)) {
    if (snap.effects) effects[uid] = snap.effects;
  }
}

function isCombatUnitUid(
  uid: string | undefined,
  playerUid: string,
  enemyUids: string[],
): uid is string {
  if (!uid || uid === "system") return false;
  if (uid.startsWith("player") || uid.startsWith("enemy")) return true;
  if (uid === playerUid) return true;
  return enemyUids.includes(uid);
}

function trackCombatUid(
  seenUids: Set<string>,
  uid: string | undefined,
  playerUid: string,
  enemyUids: string[],
) {
  if (isCombatUnitUid(uid, playerUid, enemyUids)) seenUids.add(uid);
}

/** Match backend: when mana is low, use SKILL_1 (Arrow / Slasher / …) instead of BASIC. */
export function resolveEffectiveSkill(
  intended: SkillName | undefined,
  playerMana: number,
  skillManaCosts?: Record<string, number>,
  primary: SkillName = "SKILL_1",
): SkillName {
  const raw = intended === "BASIC" || !intended ? primary : intended;
  const cost = skillManaCosts?.[raw] ?? 0;
  if (raw !== primary && cost > 0 && playerMana < cost) return primary;
  return raw;
}

const ENEMIES_PER_ROW = 3;
const FORMATION_ROW_ORDER: Formation[] = ["BACK", "MID", "FRONT"];

type EnemySizeTier = "normal" | "compact" | "dense" | "tight";

function getEnemySizeTier(totalEnemies: number, rowCount: number): EnemySizeTier {
  if (totalEnemies <= 1) return "normal";
  if (totalEnemies >= 4 || rowCount >= 2) return "tight";
  if (totalEnemies >= 3) return "dense";
  return "compact";
}

const ENEMY_CARD_WIDTH: Record<EnemySizeTier, string> = {
  normal: "flex-1 max-w-[120px]",
  compact: "w-[30%] min-w-[60px] max-w-[88px]",
  dense: "w-[28%] min-w-[52px] max-w-[72px]",
  tight: "w-[24%] min-w-[48px] max-w-[64px]",
};

const ENEMY_ICON_BOX: Record<EnemySizeTier, string> = {
  normal: "w-14 h-14",
  compact: "w-11 h-11",
  dense: "w-9 h-9",
  tight: "w-8 h-8",
};

const ENEMY_ICON_EMOJI: Record<EnemySizeTier, string> = {
  normal: "text-2xl",
  compact: "text-xl",
  dense: "text-lg",
  tight: "text-base",
};

const ENEMY_PORTRAIT_SIZE: Record<EnemySizeTier, string> = {
  normal: "w-20 h-24",
  compact: "w-16 h-20",
  dense: "w-12 h-16",
  tight: "w-10 h-14",
};

function chunkEnemyRows(uids: string[]): string[][] {
  const rows: string[][] = [];
  for (let i = 0; i < uids.length; i += ENEMIES_PER_ROW) {
    rows.push(uids.slice(i, i + ENEMIES_PER_ROW));
  }
  return rows;
}

/** Group enemies into visual rows: BACK (top) → MID → FRONT (bottom, hit first). */
function groupEnemyFormationRows(
  enemyUids: string[],
  unitMeta: Record<string, UnitMeta>,
): string[][] {
  const byFormation: Record<Formation, string[]> = { BACK: [], MID: [], FRONT: [] };
  let hasFormation = false;

  for (const uid of enemyUids) {
    const formation = unitMeta[uid]?.formation;
    if (formation) hasFormation = true;
    const bucket = formation ?? "FRONT";
    byFormation[bucket].push(uid);
  }

  if (!hasFormation) {
    return chunkEnemyRows(enemyUids.slice(0, ENEMIES_PER_ROW * 3));
  }

  const rows: string[][] = [];
  for (const formation of FORMATION_ROW_ORDER) {
    const uids = byFormation[formation];
    if (!uids.length) continue;
    for (let i = 0; i < uids.length; i += ENEMIES_PER_ROW) {
      rows.push(uids.slice(i, i + ENEMIES_PER_ROW));
    }
  }
  return rows;
}

/** Party allies at the bottom: FRONT (nearest foes) → MID → BACK. */
function groupAllyFormationRows(
  playerUids: string[],
  unitMeta: Record<string, UnitMeta>,
): string[][] {
  const byFormation: Record<Formation, string[]> = { FRONT: [], MID: [], BACK: [] };
  let hasFormation = false;

  for (const uid of playerUids) {
    const formation = unitMeta[uid]?.formation;
    if (formation) hasFormation = true;
    const bucket = formation ?? "FRONT";
    byFormation[bucket].push(uid);
  }

  if (!hasFormation) {
    return chunkEnemyRows(playerUids.slice(0, ENEMIES_PER_ROW * 3));
  }

  const rows: string[][] = [];
  for (const formation of ["FRONT", "MID", "BACK"] as Formation[]) {
    const uids = byFormation[formation];
    if (!uids.length) continue;
    for (let i = 0; i < uids.length; i += ENEMIES_PER_ROW) {
      rows.push(uids.slice(i, i + ENEMIES_PER_ROW));
    }
  }
  return rows;
}

function unitIsAlly(
  u: { team: number; uid: string },
  allyTeam: 0 | 1,
  multiTeamPlayers = false,
): boolean {
  if (u.uid.startsWith("player") && !multiTeamPlayers) return allyTeam === 0;
  if (u.uid.startsWith("enemy")) return false;
  return u.team === allyTeam;
}

function isPlayerCombatEvent(ev: BattleLogEvent, playerUid: string): boolean {
  if (ev.actor !== playerUid || !ev.skill) return false;
  return ev.action === "ATTACK" || ev.action === "HEAL" || ev.action === "AOE";
}

function applyPlayerCooldownFallback(
  cds: Record<string, number>,
  skill: string,
  skillCooldowns: Record<string, number> | undefined,
): Record<string, number> {
  if (!skillCooldowns) return cds;
  let next = tickSkillCooldowns(cds);
  const cd = skillCooldowns[skill] ?? 0;
  if (cd > 0) next = { ...next, [skill]: cd };
  return next;
}

export function deriveUnitState(
  log: BattleLogEvent[],
  plannedRotation?: SkillName[],
  skillNames?: Record<string, string>,
  skillManaCosts?: Record<string, number>,
  viewerPlayerUid?: string,
  allyTeam: 0 | 1 = 0,
  multiTeamPlayers = false,
  skillCooldowns?: Record<string, number>,
): UnitState {
  const hps: Record<string, number> = {};
  const maxHps: Record<string, number> = {};
  const effects: Record<string, StatusEffect[]> = {};
  const unitMeta: Record<string, UnitMeta> = {};
  let playerUid = "player_";
  let playerMana = 0;
  const seenUids = new Set<string>();
  const enemyUidsFromInit: string[] = [];
  const playerUidsFromInit: string[] = [];
  const plannedFirst = plannedRotation?.[0];
  let currentSkill: SkillName = resolveEffectiveSkill(plannedFirst, 0, skillManaCosts);
  let currentSkillDisplay = resolveSkillDisplay(currentSkill, skillNames);
  let sawPlayerSkill = false;
  let playerCooldowns: Record<string, number> = {};

  for (const ev of log) {
    if (ev.skill && (ev.actor.startsWith("player") || ev.actor === viewerPlayerUid)) {
      if (!viewerPlayerUid || ev.actor === viewerPlayerUid) {
        sawPlayerSkill = true;
        currentSkill = ev.skill as SkillName;
        if (ev.skill_display) currentSkillDisplay = ev.skill_display;
      }
    }

    if (ev.action === "INIT" && ev.units) {
      for (const u of ev.units) {
        seenUids.add(u.uid);
        hps[u.uid] = u.hp;
        maxHps[u.uid] = u.max_hp;
        if (u.effects?.length) effects[u.uid] = u.effects;
        if (unitIsAlly(u, allyTeam, multiTeamPlayers)) {
          if (!playerUidsFromInit.includes(u.uid)) playerUidsFromInit.push(u.uid);
          unitMeta[u.uid] = {
            displayName: u.display_name ?? u.name.replace(/^Hero_/, ""),
            heroClass: u.hero_class,
            gender: u.gender ?? "m",
            isPlayer: true,
            formation: u.formation ?? "FRONT",
            portraitPath: u.portrait_path ?? undefined,
          };
          if (!viewerPlayerUid || u.uid === viewerPlayerUid) {
            playerUid = u.uid;
            playerMana = u.mana;
          } else if (playerUid === "player_" && playerUidsFromInit.length === 1) {
            playerUid = u.uid;
            playerMana = u.mana;
          }
        } else if (u.team === 0 || u.team === 1 || u.uid.startsWith("enemy")) {
          enemyUidsFromInit.push(u.uid);
          unitMeta[u.uid] = {
            displayName: u.display_name ?? u.name.replace(/^Hero_/, ""),
            heroClass: u.hero_class,
            gender: u.gender ?? "m",
            isPlayer: Boolean(u.is_player),
            formation: u.formation,
            portraitPath: u.portrait_path ?? undefined,
          };
        }
      }
      continue;
    }

    if (ev.action === "JOIN" && ev.units) {
      for (const u of ev.units) {
        seenUids.add(u.uid);
        hps[u.uid] = u.hp;
        maxHps[u.uid] = u.max_hp;
        if (u.effects?.length) effects[u.uid] = u.effects;
        if (unitIsAlly(u, allyTeam, multiTeamPlayers)) {
          if (!playerUidsFromInit.includes(u.uid)) playerUidsFromInit.push(u.uid);
          unitMeta[u.uid] = {
            displayName: u.display_name ?? u.name.replace(/^Hero_/, ""),
            heroClass: u.hero_class,
            gender: u.gender ?? "m",
            isPlayer: true,
            formation: u.formation ?? "FRONT",
            portraitPath: u.portrait_path ?? undefined,
          };
        } else {
          if (!enemyUidsFromInit.includes(u.uid)) enemyUidsFromInit.push(u.uid);
          unitMeta[u.uid] = {
            displayName: u.display_name ?? u.name.replace(/^Hero_/, ""),
            heroClass: u.hero_class,
            gender: u.gender ?? "m",
            isPlayer: Boolean(u.is_player),
            formation: u.formation,
            portraitPath: u.portrait_path ?? undefined,
          };
        }
      }
      continue;
    }

    trackCombatUid(seenUids, ev.actor, playerUid, enemyUidsFromInit);
    trackCombatUid(seenUids, ev.target, playerUid, enemyUidsFromInit);
    if (
      (ev.actor.startsWith("player") || ev.actor === viewerPlayerUid) &&
      (!viewerPlayerUid || ev.actor === viewerPlayerUid)
    ) {
      playerUid = ev.actor;
    }

    if (ev.action === "ATTACK" && ev.target && ev.damage !== undefined && ev.target_hp !== undefined) {
      hps[ev.target] = ev.target_hp;
      if (!maxHps[ev.target]) maxHps[ev.target] = ev.target_hp + ev.damage;
    }

    if (ev.action === "ATTACK" && ev.multi_hit && ev.hits) {
      for (const hit of ev.hits) {
        hps[hit.target] = hit.hp;
        if (!maxHps[hit.target]) maxHps[hit.target] = hit.hp + (hit.damage ?? 0);
      }
    }

    if (ev.action === "DOT_TICK" && ev.target && ev.damage !== undefined && ev.target_hp !== undefined) {
      hps[ev.target] = ev.target_hp;
    }

    if (ev.action === "AOE" && ev.hits) {
      for (const hit of ev.hits) {
        hps[hit.target] = hit.hp;
        if (!maxHps[hit.target]) maxHps[hit.target] = hit.hp + (hit.damage ?? 0);
      }
    }

    applySnapshotEffects(effects, ev.snapshots);

    if (ev.snapshots) {
      for (const [uid, snap] of Object.entries(ev.snapshots)) {
        hps[uid] = snap.hp;
        if (uid.startsWith("player") || playerUidsFromInit.includes(uid)) {
          if (!viewerPlayerUid || uid === viewerPlayerUid) {
            playerUid = uid;
            playerMana = snap.mana;
          }
        }
      }
      const focusSnap = ev.snapshots[playerUid];
      if (focusSnap) {
        if (focusSnap.skill_cooldown_remaining != null) {
          playerCooldowns = { ...focusSnap.skill_cooldown_remaining };
        } else if (isPlayerCombatEvent(ev, playerUid)) {
          playerCooldowns = applyPlayerCooldownFallback(
            playerCooldowns,
            ev.skill!,
            skillCooldowns,
          );
        }
      }
    } else if (isPlayerCombatEvent(ev, playerUid)) {
      playerCooldowns = applyPlayerCooldownFallback(playerCooldowns, ev.skill!, skillCooldowns);
    }
  }

  const enemyUids =
    enemyUidsFromInit.length > 0
      ? enemyUidsFromInit
      : Array.from(seenUids).filter((u) => u.startsWith("enemy") || (!u.startsWith("player") && u !== playerUid));

  const playerUids =
    playerUidsFromInit.length > 0
      ? playerUidsFromInit
      : playerUid !== "player_"
        ? [playerUid]
        : [];

  const hasJoin = log.some((ev) => ev.action === "JOIN");
  // Keep lethal-hit targets visible for this tick so floating damage can render.
  const keepCorpse = new Set<string>();
  const lastEv = log[log.length - 1];
  if (lastEv) {
    if ((lastEv.action === "ATTACK" || lastEv.action === "DOT_TICK") && lastEv.target) {
      keepCorpse.add(lastEv.target);
    }
    if ((lastEv.action === "ATTACK" || lastEv.action === "AOE") && lastEv.hits) {
      for (const hit of lastEv.hits) {
        if (hit.target) keepCorpse.add(hit.target);
      }
    }
  }
  const visiblePlayers = hasJoin
    ? playerUids.filter((uid) => (hps[uid] ?? 0) > 0 || keepCorpse.has(uid))
    : playerUids;
  const visibleEnemies = hasJoin
    ? enemyUids.filter((uid) => (hps[uid] ?? 0) > 0 || keepCorpse.has(uid))
    : enemyUids;

  const focusUid = viewerPlayerUid && visiblePlayers.includes(viewerPlayerUid)
    ? viewerPlayerUid
    : viewerPlayerUid && playerUids.includes(viewerPlayerUid)
      ? viewerPlayerUid
      : visiblePlayers[0] ?? playerUid;

  if (!sawPlayerSkill && plannedFirst) {
    currentSkill = resolveEffectiveSkill(plannedFirst, playerMana, skillManaCosts);
    currentSkillDisplay = resolveSkillDisplay(currentSkill, skillNames);
  }

  return {
    hps,
    maxHps,
    playerUid: focusUid,
    // With JOIN wars, never fall back to corpses while waiting for substitutes.
    playerUids: hasJoin ? visiblePlayers : visiblePlayers.length ? visiblePlayers : playerUids,
    allyRosterUids: playerUids,
    hasJoin,
    playerMana: hps[focusUid] !== undefined ? playerMana : playerMana,
    enemyUids: hasJoin ? visibleEnemies : visibleEnemies.length ? visibleEnemies : enemyUids,
    unitMeta,
    currentSkill,
    currentSkillDisplay,
    playerCooldowns,
    effects,
  };
}

/** Snapshot skill bar at fight start; grow if seed improves, never shrink mid-replay. */
function useFrozenBattleSkills(visibleLog: BattleLogEvent[], seed: SkillName[]): SkillName[] {
  const [frozen, setFrozen] = useState<SkillName[]>([]);

  useEffect(() => {
    if (visibleLog.length === 0) {
      setFrozen([]);
      return;
    }
    setFrozen((prev) => growFrozenBattleSkills(prev, seed));
  }, [visibleLog.length, seed]);

  return mergeFrozenBattleSkills(frozen, seed);
}

/** Spawns floating damage/heal numbers for newly-revealed events in a growing log. */
export function useFloatingNumbers(visibleLog: BattleLogEvent[]) {
  const prevLogLenRef = useRef(0);
  const [floatingNumbers, setFloatingNumbers] = useState<FloatingNumber[]>([]);

  useEffect(() => {
    const newEvents = visibleLog.slice(prevLogLenRef.current);
    prevLogLenRef.current = visibleLog.length;
    if (!newEvents.length) return;

    const spawned: FloatingNumber[] = [];

    for (const ev of newEvents) {
      if (ev.action === "ATTACK" && ev.multi_hit && ev.hits) {
        ev.hits.forEach((hit, i) => {
          if (!hit.damage) return;
          spawned.push({
            id: `${ev.turn}-${ev.actor}-multi-${i}-${hit.target}`,
            uid: hit.target,
            value: hit.damage,
            kind: "damage",
            isCrit: Boolean(hit.is_crit),
            damageType: hit.damage_type ?? ev.damage_type ?? "physical",
          });
        });
      } else if (ev.action === "ATTACK" && ev.target && ev.damage) {
        spawned.push({
          id: `${ev.turn}-${ev.actor}-${ev.target}`,
          uid: ev.target,
          value: ev.damage,
          kind: "damage",
          isCrit: Boolean(ev.is_crit),
          damageType: ev.damage_type ?? "physical",
        });
      } else if (ev.action === "AOE" && ev.hits) {
        ev.hits.forEach((hit, i) => {
          if (!hit.damage) return;
          spawned.push({
            id: `${ev.turn}-${ev.actor}-aoe-${i}-${hit.target}`,
            uid: hit.target,
            value: hit.damage,
            kind: "damage",
            isCrit: Boolean(hit.is_crit),
            damageType: hit.damage_type ?? ev.damage_type ?? "physical",
          });
        });
      } else if (ev.action === "HEAL") {
        if (ev.mass && ev.hits) {
          ev.hits.forEach((hit, i) => {
            if (!hit.value) return;
            spawned.push({
              id: `${ev.turn}-${ev.actor}-heal-${i}`,
              uid: hit.target,
              value: hit.value,
              kind: "heal",
              isCrit: Boolean(hit.is_crit),
            });
          });
        } else if (ev.target && ev.value) {
          spawned.push({
            id: `${ev.turn}-${ev.actor}-heal-${ev.target}`,
            uid: ev.target,
            value: ev.value,
            kind: "heal",
            isCrit: Boolean(ev.is_crit),
          });
        }
      } else if (ev.action === "DOT_TICK" && ev.target && ev.damage) {
        spawned.push({
          id: `${ev.turn}-dot-${ev.target}-${ev.effect_id}`,
          uid: ev.target,
          value: ev.damage,
          kind: "damage",
          isCrit: false,
          damageType: ev.damage_type ?? "physical",
        });
      }
    }

    if (spawned.length) {
      setFloatingNumbers((prev) => [...prev, ...spawned]);
      const ids = new Set(spawned.map((d) => d.id));
      setTimeout(() => setFloatingNumbers((prev) => prev.filter((d) => !ids.has(d.id))), DMG_LIFETIME_MS);
    }
  }, [visibleLog]);

  return floatingNumbers;
}

/** Brief holy flash on units when buffs/debuffs are stripped (Purify, Purification). */
export function useDispelFlashes(visibleLog: BattleLogEvent[]) {
  const prevLogLenRef = useRef(0);
  const [flashes, setFlashes] = useState<Record<string, number>>({});

  useEffect(() => {
    const newEvents = visibleLog.slice(prevLogLenRef.current);
    prevLogLenRef.current = visibleLog.length;
    if (!newEvents.length) return;

    const triggered: Record<string, number> = {};
    const now = Date.now();
    for (const ev of newEvents) {
      if (ev.action !== "BUFF_REMOVE" || !ev.hits) continue;
      for (const hit of ev.hits) {
        if (hit.target) triggered[hit.target] = now;
      }
    }
    if (!Object.keys(triggered).length) return;

    setFlashes((prev) => ({ ...prev, ...triggered }));
    const ids = Object.keys(triggered);
    setTimeout(() => {
      setFlashes((prev) => {
        const next = { ...prev };
        for (const uid of ids) delete next[uid];
        return next;
      });
    }, 900);
  }, [visibleLog]);

  return flashes;
}

/* ══════════════════════════════════════════════════════════════════════════
 * BattleArena — renders the enemies row, hero portrait, and status bar for
 * a (progressively revealed) battle log. Presentation-only: callers own the
 * replay ticking and any result overlay.
 * ══════════════════════════════════════════════════════════════════════════ */
interface BattleArenaProps {
  visibleLog: BattleLogEvent[];
  heroClass: HeroClass | undefined;
  heroName?: string;
  fallbackMaxHp?: number;
  battleType?: string;
  opponentFallback?: UnitMeta;
  /** Hero rotation order — used to preview the first skill before the log catches up. */
  plannedRotation?: SkillName[];
  /** Complete battle log (not replay slice) — seeds skill bar with all fight skills from turn 0. */
  fullBattleLog?: BattleLogEvent[];
  /** Local player uid from log, e.g. player_42 */
  viewerPlayerUid?: string;
  /** Which log team renders as bottom allies (0=default attackers, 1=defenders). */
  allyTeam?: 0 | 1;
  /** Force card party layout (guild line wars) — never solo hero portrait. */
  forcePartyMode?: boolean;
  /** MP dungeon party roster for names/classes when log uses generic Hero_* names. */
  partyMembers?: Array<{
    user_id: number;
    name: string;
    hero_class: HeroClass;
    gender?: HeroGender;
  }>;
  /** e.g. "Floor 20", stage name, "MP Lv.5 (hard)" */
  battleContextLabel?: string;
  /** Default "BATTLE" */
  battleContextTitle?: string;
}

export function BattleArena({
  visibleLog,
  heroClass,
  heroName,
  fallbackMaxHp = 1000,
  battleType,
  opponentFallback,
  plannedRotation,
  fullBattleLog,
  viewerPlayerUid,
  allyTeam = 0,
  forcePartyMode = false,
  partyMembers,
  battleContextLabel,
  battleContextTitle = "BATTLE",
}: BattleArenaProps) {
  const clientConfig = useGameStore((s) => s.clientConfig);
  const skillNames = useGameStore((s) => s.skillNames);
  const skillManaCosts = useGameStore((s) => s.skillManaCosts);
  const skillCooldowns = useGameStore((s) => s.skillCooldowns);
  const hero = useGameStore((s) => s.hero);
  const heroSnapshot = useGameStore((s) => s.heroSnapshot);
  const buffDefinitions = clientConfig?.buff_definitions;
  useBattleHaptics(visibleLog, clientConfig?.combat_haptics);
  const flyAnimations = useBuffFlyAnimations(visibleLog);
  const floatingNumbers = useFloatingNumbers(visibleLog);
  const dispelFlashes = useDispelFlashes(visibleLog);
  const multiTeamPlayers = battleType === "ARENA_PARTY";
  const rotationForBar = useMemo(
    () => mergeRotationSources([plannedRotation, heroSnapshot?.rotation, hero?.rotation]),
    [plannedRotation, heroSnapshot?.rotation, hero?.rotation],
  );
  const skillBarSeed = useMemo(
    () => battleBarSkillSeed(rotationForBar, fullBattleLog ?? visibleLog, viewerPlayerUid),
    [rotationForBar, fullBattleLog, visibleLog, viewerPlayerUid],
  );
  const battleSkills = useFrozenBattleSkills(visibleLog, skillBarSeed);
  const unitState = useMemo(() => {
    const state = deriveUnitState(
      visibleLog,
      rotationForBar,
      skillNames,
      skillManaCosts,
      viewerPlayerUid,
      allyTeam,
      multiTeamPlayers,
      skillCooldowns,
    );
    if (partyMembers?.length) {
      for (const member of partyMembers) {
        const uid = `player_${member.user_id}`;
        state.unitMeta[uid] = {
          ...state.unitMeta[uid],
          displayName: member.name,
          heroClass: member.hero_class,
          gender: member.gender ?? state.unitMeta[uid]?.gender ?? "m",
          isPlayer: true,
          formation: state.unitMeta[uid]?.formation ?? "FRONT",
        };
      }
    }
    return state;
  }, [
    visibleLog,
    rotationForBar,
    skillNames,
    skillManaCosts,
    skillCooldowns,
    viewerPlayerUid,
    allyTeam,
    multiTeamPlayers,
    partyMembers,
  ]);

  const partyMode =
    forcePartyMode ||
    battleType === "MP_DUNGEON" ||
    battleType === "ARENA_PARTY" ||
    unitState.hasJoin ||
    unitState.allyRosterUids.length > 1;
  const focusUid = unitState.playerUid;
  const focusMeta = unitState.unitMeta[focusUid];
  const statusHeroClass = focusMeta?.heroClass ?? heroClass;
  const statusHeroGender =
    focusMeta?.gender ?? hero?.gender ?? heroSnapshot?.gender ?? "m";
  const statusHeroName = focusMeta?.displayName ?? heroName ?? heroClass ?? "Hero";

  const playerMaxHp = unitState.maxHps[focusUid] ?? fallbackMaxHp;
  const playerHp = unitState.hps[focusUid] ?? playerMaxHp;
  const heroImageSrc =
    !partyMode && statusHeroClass
      ? getHeroImagePath(statusHeroClass, statusHeroGender)
      : undefined;
  const skillDisplay = resolveSkillDisplay(
    unitState.currentSkill,
    skillNames,
    unitState.currentSkillDisplay,
  );

  const playerNumbers = floatingNumbers.filter((d) => d.uid === focusUid);
  const playerEffects = unitState.effects[focusUid] ?? [];
  const playerDispel = Boolean(dispelFlashes[focusUid]);
  const enemyRows = useMemo(
    () => groupEnemyFormationRows(unitState.enemyUids, unitState.unitMeta),
    [unitState.enemyUids, unitState.unitMeta],
  );
  const allyRows = useMemo(
    () => (partyMode ? groupAllyFormationRows(unitState.playerUids, unitState.unitMeta) : []),
    [partyMode, unitState.playerUids, unitState.unitMeta],
  );
  const allySizeTier = getEnemySizeTier(unitState.playerUids.length, allyRows.length);
  const enemySizeTier = getEnemySizeTier(unitState.enemyUids.length, enemyRows.length);
  const enemyRowGap = enemySizeTier === "tight" ? "gap-0.5" : enemySizeTier === "dense" ? "gap-1" : "gap-1.5";
  const enemyCardGap = enemySizeTier === "tight" || enemySizeTier === "dense" ? "gap-1" : "gap-2";

  return (
    <div className="relative flex flex-col h-full bg-kingdom-dark select-none">
      <BuffFlyOverlay animations={flyAnimations} enemyUids={unitState.enemyUids} />
      {battleContextLabel && (
        <BattleContextHeader label={battleContextLabel} title={battleContextTitle} />
      )}
      {/* ── Enemies — max 3 per row ── */}
      <div className={clsx("relative z-20 shrink-0 flex flex-col items-center px-2 pb-1 pt-4", enemyRowGap)}>
        {enemyRows.map((row, rowIdx) => (
          <div key={rowIdx} className={clsx("flex items-end justify-center w-full max-w-sm", enemyCardGap)}>
            {row.map((uid) => {
              const hp = unitState.hps[uid] ?? unitState.maxHps[uid] ?? 1;
              const maxHp = unitState.maxHps[uid] ?? Math.max(1, hp);
              const meta = unitState.unitMeta[uid] ?? opponentFallback;
              return (
                <EnemyCard
                  key={uid}
                  uid={uid}
                  hp={Math.max(0, hp)}
                  maxHp={maxHp}
                  numbers={floatingNumbers.filter((d) => d.uid === uid)}
                  effects={unitState.effects[uid] ?? []}
                  buffDefinitions={buffDefinitions}
                  alive={hp > 0}
                  dispelFlash={Boolean(dispelFlashes[uid])}
                  opponentMeta={meta}
                  showHeroPortrait={Boolean(meta?.isPlayer || battleType === "PVP")}
                  sizeTier={enemySizeTier}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* ── Hero image (solo) or flex spacer (party) ── */}
      <div className="relative z-0 flex-1 flex flex-col items-center justify-end min-h-0 pb-1 overflow-hidden">
        {partyMode ? (
          <div
            className={clsx(
              "relative z-10 w-full max-w-sm px-2 flex flex-col items-center justify-end",
              allySizeTier === "tight" ? "gap-0.5" : "gap-1",
            )}
          >
            {allyRows.map((row, rowIdx) => (
              <div
                key={`ally-row-${rowIdx}`}
                className={clsx(
                  "flex items-end justify-center w-full",
                  allySizeTier === "tight" ? "gap-1" : "gap-2",
                )}
              >
                {row.map((uid) => {
                  const hp = unitState.hps[uid] ?? unitState.maxHps[uid] ?? 1;
                  const maxHp = unitState.maxHps[uid] ?? Math.max(1, hp);
                  const meta = unitState.unitMeta[uid];
                  const isSelf = uid === focusUid;
                  return (
                    <EnemyCard
                      key={uid}
                      uid={uid}
                      hp={Math.max(0, hp)}
                      maxHp={maxHp}
                      numbers={floatingNumbers.filter((d) => d.uid === uid)}
                      effects={unitState.effects[uid] ?? []}
                      buffDefinitions={buffDefinitions}
                      alive={hp > 0}
                      dispelFlash={Boolean(dispelFlashes[uid])}
                      opponentMeta={meta}
                      showHeroPortrait
                      sizeTier={allySizeTier}
                      highlight={isSelf}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        ) : (
          heroImageSrc && (
            <img
              src={heroImageSrc}
              alt={statusHeroClass}
              className="relative z-0 max-h-full w-auto object-contain object-bottom drop-shadow-[0_4px_24px_rgba(167,139,250,0.4)]"
              style={{ maxHeight: "100%" }}
            />
          )
        )}
      </div>

      {/* Damage/heal on hero — low on portrait, just above status bar */}
      <div className="pointer-events-none absolute inset-x-0 z-40 bottom-[12.5rem] flex flex-col items-center justify-end gap-0.5 min-h-[3rem]">
        {playerDispel && (
          <div className="animate-pulse text-2xl drop-shadow-[0_0_8px_rgba(255,255,200,0.9)]">✨</div>
        )}
        {playerNumbers.map((num) => (
          <CombatNumber key={num.id} num={num} variant="hero" />
        ))}
      </div>

      {/* ── Status bar ── */}
      <StatusBar
        heroName={statusHeroName}
        hp={Math.max(0, playerHp)}
        maxHp={playerMaxHp}
        mana={Math.min(100, Math.max(0, unitState.playerMana))}
        skill={unitState.currentSkill}
        skillDisplay={skillDisplay}
        heroClass={statusHeroClass}
        battleSkills={battleSkills}
        playerCooldowns={unitState.playerCooldowns}
        effects={playerEffects}
        buffDefinitions={buffDefinitions}
        dispelFlash={playerDispel}
        skillNames={skillNames}
        skillManaCosts={skillManaCosts}
      />
    </div>
  );
}

/* ─── Monster portrait (PNG with emoji fallback) ─────────────────────────── */
function MonsterPortrait({
  src,
  alt,
  alive,
  emojiClassName,
}: {
  src: string;
  alt: string;
  alive: boolean;
  emojiClassName: string;
}) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (failed) {
    return (
      <div
        className={clsx(
          "rounded-xl flex items-center justify-center border-2 w-full h-full",
          emojiClassName,
          alive ? "bg-rose-950 border-rose-700" : "bg-gray-900 border-gray-700",
        )}
      >
        {alive ? "👹" : "💀"}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "rounded-xl overflow-hidden border-2 relative z-0 bg-kingdom-dark w-full h-full",
        alive ? "border-rose-700" : "border-gray-700",
      )}
    >
      <img
        src={staticAssetUrl(src)}
        alt={alt}
        className={clsx(
          "w-full h-full object-contain object-bottom",
          !alive && "grayscale brightness-75",
        )}
        onError={() => setFailed(true)}
      />
      {!alive && (
        <span
          className="absolute inset-0 flex items-center justify-center text-2xl pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
          aria-hidden
        >
          💀
        </span>
      )}
    </div>
  );
}

/* ─── Enemy card ─────────────────────────────────────────────────────────── */
function EnemyCard({
  uid,
  hp,
  maxHp,
  numbers,
  effects,
  buffDefinitions,
  alive,
  dispelFlash = false,
  opponentMeta,
  showHeroPortrait = false,
  sizeTier = "normal",
  highlight = false,
}: {
  uid: string;
  hp: number;
  maxHp: number;
  numbers: FloatingNumber[];
  effects: StatusEffect[];
  buffDefinitions?: Record<string, BuffDefinition> | null;
  alive: boolean;
  dispelFlash?: boolean;
  opponentMeta?: UnitMeta;
  showHeroPortrait?: boolean;
  sizeTier?: EnemySizeTier;
  highlight?: boolean;
}) {
  const pct = maxHp > 0 ? Math.round((hp / maxHp) * 100) : 0;
  const label = opponentMeta?.displayName
    ?? uid
      .replace("enemy_", "E")
      .replace("dungeon_enemy", "Boss")
      .replace("campaign_enemy", "Boss")
      .replace("catacomb_boss", "Boss")
      .replace(/^skeleton_(\d+)$/, (_, n) => `Skeleton ${Number(n) + 1}`);
  const heroImageSrc =
    showHeroPortrait && opponentMeta
      ? getHeroImagePath(opponentMeta.heroClass, opponentMeta.gender)
      : undefined;
  const monsterPortraitSrc =
    !showHeroPortrait && opponentMeta?.portraitPath ? opponentMeta.portraitPath : undefined;
  const iconBoxClass = ENEMY_ICON_BOX[sizeTier];
  const iconEmojiClass = ENEMY_ICON_EMOJI[sizeTier];
  const fadeWhenDead = !alive && !monsterPortraitSrc;

  return (
    <div
      className={clsx(
        "relative flex flex-col items-center gap-1",
        ENEMY_CARD_WIDTH[sizeTier],
        fadeWhenDead && "opacity-40",
        highlight && "ring-2 ring-kingdom-glow rounded-xl",
      )}
    >
      {dispelFlash && (
        <div className="pointer-events-none absolute -top-1 inset-x-0 z-30 flex justify-center animate-pulse">
          <span className="text-lg drop-shadow-[0_0_6px_rgba(255,255,200,0.9)]">✨</span>
        </div>
      )}
      <div className={clsx("relative shrink-0", showHeroPortrait ? ENEMY_PORTRAIT_SIZE[sizeTier] : iconBoxClass)}>
        {heroImageSrc ? (
          <div
            className={clsx(
              "rounded-xl overflow-hidden border-2 relative z-0 bg-kingdom-dark w-full h-full",
              alive ? "border-kingdom-glow/70" : "border-gray-700 grayscale",
            )}
          >
            <img
              src={heroImageSrc}
              alt={label}
              className="w-full h-full object-cover object-top scale-110"
            />
          </div>
        ) : monsterPortraitSrc ? (
          <MonsterPortrait
            src={monsterPortraitSrc}
            alt={label}
            alive={alive}
            emojiClassName={iconEmojiClass}
          />
        ) : (
          <div
            className={clsx(
              "rounded-xl flex items-center justify-center border-2 relative z-0 w-full h-full",
              iconEmojiClass,
              alive ? "bg-rose-950 border-rose-700" : "bg-gray-900 border-gray-700",
            )}
          >
            {alive ? "👹" : "💀"}
          </div>
        )}
        <div className="absolute inset-x-0 top-5 flex flex-col items-center pointer-events-none z-30">
          {numbers.map((num) => (
            <CombatNumber key={num.id} num={num} variant="enemy" />
          ))}
        </div>
      </div>

      <span
        className={clsx(
          "text-center leading-tight font-semibold w-full px-0.5 text-[10px] line-clamp-2",
          opponentMeta && showHeroPortrait ? "text-gold-300" : "text-gray-400",
        )}
      >
        {label}
      </span>

      {/* HP bar */}
      <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${pct}%`,
            background: pct > 50 ? "#22c55e" : pct > 25 ? "#f59e0b" : "#ef4444",
          }}
        />
      </div>
      <span className="text-[9px] text-gray-500">
        {hp}/{maxHp}
      </span>
      <BuffBar effects={effects} definitions={buffDefinitions} size="sm" className="min-h-[1.75rem]" />
    </div>
  );
}

/* ─── Floating damage / heal number ─────────────────────────────────────── */
function CombatNumber({ num, variant = "hero" }: { num: FloatingNumber; variant?: "hero" | "enemy" }) {
  const isHeal = num.kind === "heal";
  const isMagical = num.damageType === "magical";
  const prefix = isHeal ? "+" : num.isCrit ? "💥" : "";

  return (
    <span
      className={clsx(
        "pointer-events-none leading-none",
        num.isCrit ? "font-metal tracking-wide text-outline-black" : "font-black",
        num.isCrit
          ? isMagical
            ? "text-xl text-blue-400"
            : "text-xl text-red-500"
          : isMagical
            ? "text-lg text-blue-300 [text-shadow:0_1px_3px_#000]"
            : "",
        variant === "enemy" ? "animate-float-up-sm text-sm" : "animate-float-up-hero text-lg",
        !num.isCrit && !isHeal && !isMagical && (variant === "enemy" ? "text-sm" : "text-lg"),
        num.isCrit
          ? isHeal
            ? "text-emerald-400"
            : undefined
          : isHeal
            ? "text-emerald-400 [text-shadow:0_1px_3px_#000]"
            : isMagical
              ? undefined
              : "text-yellow-300 [text-shadow:0_1px_3px_#000]",
      )}
    >
      {prefix}
      <FormattedNumber value={num.value} />
    </span>
  );
}

/* ─── Battle context header (floor / stage) ─────────────────────────────── */
function BattleContextHeader({ label, title }: { label?: string; title: string }) {
  return (
    <div className="shrink-0 text-center pt-2 pb-1 px-2 border-b border-kingdom-mid/40">
      {label && (
        <div className="text-[11px] text-gray-400 tracking-wide truncate">{label}</div>
      )}
      <div className="text-base font-game font-bold text-gold-400 tracking-[0.2em] uppercase mt-0.5">
        {title}
      </div>
    </div>
  );
}

/* ─── Status bar at bottom ──────────────────────────────────────────────── */
function SkillBar({
  heroClass,
  skills,
  activeSkill,
  cooldowns,
  skillNames,
  skillManaCosts,
  activeLabel,
}: {
  heroClass: HeroClass;
  skills: SkillName[];
  activeSkill: SkillName;
  cooldowns: Record<string, number>;
  skillNames?: Record<string, string>;
  skillManaCosts?: Record<string, number>;
  activeLabel: string;
}) {
  if (!skills.length) {
    return (
      <div className="flex items-center gap-2 pt-1">
        <SkillIcon heroClass={heroClass} skill={activeSkill} className="w-10 h-10" alt={activeSkill} />
        <div className="min-w-0">
          <div className="text-[10px] text-gray-500 leading-none">Skill</div>
          <div className="text-xs font-bold text-gold-300 leading-tight truncate">{activeLabel}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-1">
      <div className="flex flex-nowrap items-start gap-1.5 overflow-x-auto max-w-full pb-0.5 -mx-0.5 px-0.5">
        {skills.map((skill) => {
          const cd = cooldowns[skill] ?? 0;
          const isActive = skill === activeSkill;
          const label = resolveSkillDisplay(skill, skillNames);
          const mp = skillManaCosts?.[skill] ?? 0;
          return (
            <div
              key={skill}
              className={clsx(
                "relative flex flex-col items-center shrink-0 w-[4.25rem]",
                isActive && "opacity-100",
              )}
            >
              <div
                className={clsx(
                  "relative box-border flex items-center justify-center rounded-lg w-11 h-11 border-2 bg-kingdom-dark/60",
                  isActive ? "border-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.35)]" : "border-kingdom-mid/60",
                )}
              >
                <SkillIcon heroClass={heroClass} skill={skill} className="w-9 h-9" alt={skill} />
                {cd > 0 && (
                  <>
                    <div className="absolute inset-0 rounded-lg bg-black/55 pointer-events-none" />
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-red-500 tabular-nums pointer-events-none">
                      {cd}
                    </span>
                  </>
                )}
              </div>
              <span className="text-[9px] text-gray-200 font-semibold truncate w-full text-center mt-0.5 leading-tight">
                {label}
              </span>
              <span className="text-[8px] text-blue-300/90 tabular-nums">MP {mp}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatBarInlay({
  pct,
  text,
  variant,
}: {
  pct: number;
  text: string;
  variant: "hp" | "mp";
}) {
  const fill =
    variant === "hp"
      ? "bg-gradient-to-r from-emerald-700 to-emerald-400"
      : "bg-gradient-to-r from-blue-700 to-blue-400";
  const border = variant === "hp" ? "border-emerald-900/50" : "border-blue-900/50";

  return (
    <div
      className={clsx(
        "relative flex-1 h-4 rounded-full overflow-hidden bg-gray-900/90 border",
        border,
      )}
    >
      <div
        className={clsx("h-full rounded-full transition-all duration-300", fill)}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white tabular-nums drop-shadow-[0_1px_2px_rgba(0,0,0,0.95)]">
        {text}
      </span>
    </div>
  );
}

function StatusBar({
  heroName,
  hp,
  maxHp,
  mana,
  skill,
  skillDisplay,
  heroClass,
  battleSkills,
  playerCooldowns,
  effects,
  buffDefinitions,
  dispelFlash = false,
  skillNames,
  skillManaCosts,
}: {
  heroName: string;
  hp: number;
  maxHp: number;
  mana: number;
  skill: SkillName;
  skillDisplay: string;
  heroClass?: HeroClass;
  battleSkills: SkillName[];
  playerCooldowns: Record<string, number>;
  effects: StatusEffect[];
  buffDefinitions?: Record<string, BuffDefinition> | null;
  dispelFlash?: boolean;
  skillNames?: Record<string, string>;
  skillManaCosts?: Record<string, number>;
}) {
  const hpPct = maxHp > 0 ? (hp / maxHp) * 100 : 0;

  return (
    <div
      className={clsx(
        "flex-none bg-gradient-to-t from-kingdom-dark via-kingdom-purple/90 to-kingdom-purple/70 border-t border-kingdom-light/60 px-3 py-2 space-y-1.5",
        dispelFlash && "ring-2 ring-yellow-200/40 ring-inset",
      )}
    >
      {heroClass && (
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl shrink-0 leading-none" aria-hidden>
            {CLASS_CREST[heroClass] ?? "⚔️"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-white truncate leading-tight">{heroName}</div>
            <div className="text-[10px] text-gray-400 leading-tight">{CLASS_INFO[heroClass].title}</div>
          </div>
        </div>
      )}
      {!heroClass && (
        <span className="text-sm font-bold text-white tracking-wide truncate block">{heroName}</span>
      )}

      <BuffBar effects={effects} definitions={buffDefinitions} className="min-h-[1.75rem] pb-0.5" />

      <StatBarInlay pct={hpPct} text={`${hp}/${maxHp}`} variant="hp" />
      <StatBarInlay pct={mana} text={`${mana}/100`} variant="mp" />

      {heroClass && (
        <SkillBar
          heroClass={heroClass}
          skills={battleSkills}
          activeSkill={skill}
          cooldowns={playerCooldowns}
          skillNames={skillNames}
          skillManaCosts={skillManaCosts}
          activeLabel={skillDisplay}
        />
      )}
    </div>
  );
}
